import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import { nanoid } from 'nanoid';
import { Chat } from '../components/Chat/Chat';
import { ChatButton } from '../components/ChatButton';
import { createApolloClient } from './createApolloClient';
import { SessionManager } from './sessionManager';
import { ThemeProvider } from './ThemeContext';
import { useChatStore } from '../stores/chatStore';
import { detectLocale, getTranslations } from './i18n';
import type { WebChatWidgetProps } from './types';
import './widget.css';

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | null;

export const WebChatWidget: React.FC<WebChatWidgetProps> = ({
  httpUrl,
  wsUrl,
  companyPublicId,
  size = {},
  theme,
  headerText,
  headerSubtitle,
  avatarUrl,
  enableNotifications = false,
  onMessageSent,
  onError,
  onMessageReceived,
  sessionId: customSessionId,
  storagePrefix = 'web-chat',
  className = '',
  style = {},
  defaultOpen = false,
  position = 'bottom-right',
  buttonSize = 60,
  expandedWidth,
  expandedHeight,
  telegramBotUrl,
  locale: localeProp,
  privacyPolicyUrl,
  termsOfServiceUrl,
  user,
}) => {
  const locale = localeProp || detectLocale();
  const t = useMemo(() => getTranslations(locale), [locale]);
  // Widget open/close state
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Get reset function from chat store
  const resetStore = useChatStore((state) => state.reset);

  // Pre-authorize user if user prop is provided
  const setUserInfo = useChatStore((state) => state.setUserInfo);
  useEffect(() => {
    if (user?.firstName && user?.email) {
      setUserInfo({ firstName: user.firstName, email: user.email });
    }
  }, [user?.firstName, user?.email, setUserInfo]);

  const expandedRef = useRef<HTMLDivElement>(null);

  // Мобильный фулскрин: пока чат развёрнут — блокируем скролл хост-страницы,
  // а высоту и смещение держим по visualViewport, чтобы поле ввода не пряталось
  // за экранной клавиатурой (fixed + height:100% под клавиатуру не сжимается).
  // Inline-important, потому что базовую высоту задаёт media-правило с !important.
  useEffect(() => {
    if (!isOpen) return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const el = expandedRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const vv = window.visualViewport;
    const apply = () => {
      if (!el || !vv) return;
      el.style.setProperty('height', `${Math.round(vv.height)}px`, 'important');
      el.style.setProperty('top', `${Math.round(vv.offsetTop)}px`, 'important');
    };
    apply();
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    return () => {
      document.body.style.overflow = prevOverflow;
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      if (el) {
        el.style.removeProperty('height');
        el.style.removeProperty('top');
      }
    };
  }, [isOpen]);

  // Resize state
  const [dimensions, setDimensions] = useState({
    width: parseInt(String(expandedWidth || size.width || '400'), 10) || 400,
    height: parseInt(String(expandedHeight || size.height || '600'), 10) || 600,
  });
  const [resizing, setResizing] = useState<ResizeDirection>(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Drag state
  const [dragging, setDragging] = useState(false);
  const [widgetPosition, setWidgetPosition] = useState<{ x: number; y: number } | null>(null);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const MIN_WIDTH = 300;
  const MIN_HEIGHT = 400;

  const handleResizeStart = useCallback(
    (direction: ResizeDirection, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(direction);
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        width: dimensions.width,
        height: dimensions.height,
      };
    },
    [dimensions],
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      // Only start drag if clicking directly on header area (not buttons)
      if ((e.target as HTMLElement).closest('button')) return;

      e.preventDefault();
      setDragging(true);

      // On first drag, compute actual position from DOM instead of using hardcoded fallback
      let currentX = widgetPosition?.x ?? 0;
      let currentY = widgetPosition?.y ?? 0;

      if (!widgetPosition) {
        const widgetEl = (e.target as HTMLElement).closest('.wc-chat-expanded');
        if (widgetEl) {
          const rect = widgetEl.getBoundingClientRect();
          currentX = window.innerWidth - rect.right;
          currentY = window.innerHeight - rect.bottom;
          setWidgetPosition({ x: currentX, y: currentY });
        }
      }

      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        posX: currentX,
        posY: currentY,
      };
    },
    [widgetPosition],
  );

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;
      const maxWidth = window.innerWidth * 0.9;
      const maxHeight = window.innerHeight * 0.9;

      let newWidth = startPos.current.width;
      let newHeight = startPos.current.height;

      // Handle horizontal resize
      if (resizing.includes('e')) {
        newWidth = startPos.current.width + deltaX;
      } else if (resizing.includes('w')) {
        newWidth = startPos.current.width - deltaX;
      }

      // Handle vertical resize
      if (resizing.includes('s')) {
        newHeight = startPos.current.height + deltaY;
      } else if (resizing.includes('n')) {
        newHeight = startPos.current.height - deltaY;
      }

      // Apply constraints
      newWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth));
      newHeight = Math.max(MIN_HEIGHT, Math.min(maxHeight, newHeight));

      setDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  // Drag effect
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;

      let newX = dragStart.current.posX - deltaX;
      let newY = dragStart.current.posY - deltaY;

      // Keep widget within viewport
      newX = Math.max(10, Math.min(window.innerWidth - dimensions.width - 10, newX));
      newY = Math.max(10, Math.min(window.innerHeight - dimensions.height - 10, newY));

      setWidgetPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dimensions.width, dimensions.height]);

  // Create session manager
  const sessionManager = useMemo(() => new SessionManager(storagePrefix), [storagePrefix]);

  // Get or create session ID
  const [sessionId, setSessionId] = useState(() => sessionManager.getOrCreateSessionId(customSessionId));

  // Create Apollo Client
  const apolloClient = useMemo(() => {
    return createApolloClient({
      httpUrl,
      wsUrl,
      getSessionId: () => sessionId,
      companyPublicId,
    });
  }, [httpUrl, wsUrl, sessionId, companyPublicId]);

  // Start a new chat session — clears messages, generates new sessionId, recreates Apollo client
  const handleNewChat = useCallback(() => {
    resetStore();
    apolloClient.resetStore().catch(() => {});
    const newSessionId = nanoid(32);
    sessionManager.setSessionId(newSessionId);
    setSessionId(newSessionId);
  }, [resetStore, apolloClient, sessionManager]);

  // Handlers
  const handleOpen = () => setIsOpen(true);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Clear chat store state to prevent message duplication
    resetStore();
    // Clear Apollo cache to prevent cached data from persisting
    apolloClient.resetStore().catch((error) => {
      console.error('[WebChatWidget] Failed to reset Apollo store:', error);
    });
  }, [resetStore, apolloClient]);

  // Derive asset base URL from httpUrl (e.g. "https://bot-dev.example.com/graphql" → "https://bot-dev.example.com")
  const assetBaseUrl = useMemo(() => {
    try {
      const url = new URL(httpUrl);
      return url.origin;
    } catch {
      return '';
    }
  }, [httpUrl]);

  // Dynamically inject @font-face for the preset font (NV: Involve, NeVi: Satoshi) with correct base URL.
  // Семейства с префиксом WC-: правило уходит в <head> хост-страницы, и если бы имя
  // совпадало с шрифтом сайта (Satoshi на nv.global), наш вариативный @font-face
  // перебивал бы статические начертания сайта и перерисовывал всю страницу.
  useEffect(() => {
    const presetFonts: Record<string, { id: string; css: string }> = {
      nv: {
        id: 'wc-involve-font',
        css: `@font-face { font-family: 'WC-Involve'; src: url('${assetBaseUrl}/widget/fonts/Involve-VF.ttf') format('truetype'); font-weight: 100 900; font-style: normal; font-display: swap; }`,
      },
      nevi: {
        id: 'wc-satoshi-font',
        css: `@font-face { font-family: 'WC-Satoshi'; src: url('${assetBaseUrl}/widget/fonts/Satoshi-VF.woff2') format('woff2'); font-weight: 300 900; font-style: normal; font-display: swap; }`,
      },
    };
    const font = theme?.preset ? presetFonts[theme.preset] : undefined;
    if (!font) return;
    if (document.getElementById(font.id)) return;
    const style = document.createElement('style');
    style.id = font.id;
    style.textContent = font.css;
    document.head.appendChild(style);
  }, [assetBaseUrl, theme?.preset]);

  // Memoize theme context value to avoid re-rendering all consumers on drag/resize
  const themeContextValue = useMemo(
    () => ({
      theme,
      headerText,
      headerSubtitle,
      avatarUrl,
      enableNotifications,
      telegramBotUrl,
      assetBaseUrl,
      locale,
      t,
      onMessageSent,
      onError,
      onMessageReceived,
      onMinimize: handleClose,
      onNewChat: handleNewChat,
      onDragStart: handleDragStart,
      privacyPolicyUrl,
      termsOfServiceUrl,
    }),
    [
      theme,
      headerText,
      headerSubtitle,
      avatarUrl,
      enableNotifications,
      telegramBotUrl,
      assetBaseUrl,
      locale,
      t,
      onMessageSent,
      onError,
      onMessageReceived,
      handleClose,
      handleNewChat,
      handleDragStart,
      privacyPolicyUrl,
      termsOfServiceUrl,
    ],
  );

  // Calculate dimensions for expanded state
  const containerStyle: React.CSSProperties = {
    width: `${dimensions.width}px`,
    height: `${dimensions.height}px`,
    ...(widgetPosition && {
      right: `${widgetPosition.x}px`,
      bottom: `${widgetPosition.y}px`,
    }),
    ...style,
  };

  // Apply custom theme CSS variables
  const themeStyle: React.CSSProperties = theme
    ? ({
        '--wc-primary-color': theme.primaryColor || '#8b5cf6',
        '--wc-background-color': theme.backgroundColor || '#1a1428',
        '--wc-header-color': theme.headerColor || '#ffffff',
        '--wc-header-background-color': theme.headerBackgroundColor || 'rgba(30, 25, 51, 0.8)',
        '--wc-user-message-gradient-start': theme.userMessageGradientStart || '#ec4899',
        '--wc-user-message-gradient-end': theme.userMessageGradientEnd || '#f97316',
        '--wc-bot-message-color': theme.botMessageColor || '#4a3c72',
        '--wc-text-color': theme.textColor || '#ffffff',
        '--wc-input-text-color': theme.inputTextColor || theme.textColor || '#ffffff',
        '--wc-placeholder-color': theme.placeholderColor || 'rgba(255, 255, 255, 0.5)',
        '--wc-bot-message-text-color': theme.botMessageTextColor || theme.textColor || '#ffffff',
        '--wc-user-message-text-color': theme.userMessageTextColor || '#ffffff',
        '--wc-user-message-time-color': theme.userMessageTimeColor || undefined,
        '--wc-bot-message-time-color': theme.botMessageTimeColor || undefined,
        '--wc-font-family': theme.fontFamily,
        '--wc-header-style': theme.headerStyle || 'glass',
        '--wc-header-glass-opacity': theme.headerGlassOpacity ?? 0.12,
      } as React.CSSProperties)
    : {};

  // Render minimized button
  if (!isOpen) {
    return (
      <div className={`web-chat-widget ${className}`}>
        <ChatButton
          onClick={handleOpen}
          position={position}
          size={buttonSize}
          primaryColor={theme?.primaryColor}
          ariaLabel={t.chatButton.openChat}
          preset={theme?.preset}
          assetBaseUrl={assetBaseUrl}
        />
      </div>
    );
  }

  // Resize handle styles
  const edgeStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
  };

  const cornerStyle: React.CSSProperties = {
    position: 'absolute',
    width: '12px',
    height: '12px',
    zIndex: 11,
  };

  // Render expanded chat window
  return (
    <div
      ref={expandedRef}
      className={`web-chat-widget wc-chat-expanded wc-position-${position} ${theme?.preset === 'nv' ? 'wc-preset-nv' : ''} ${theme?.preset === 'nevi' ? 'wc-preset-nevi' : ''} ${className} ${resizing ? 'wc-resizing' : ''} ${dragging ? 'wc-dragging' : ''}`}
      style={{ ...containerStyle, ...themeStyle }}
    >
      {/* Resize handles - edges */}
      <div
        style={{ ...edgeStyle, top: 0, left: '12px', right: '12px', height: '6px', cursor: 'n-resize' }}
        onMouseDown={(e) => handleResizeStart('n', e)}
      />
      <div
        style={{ ...edgeStyle, bottom: 0, left: '12px', right: '12px', height: '6px', cursor: 's-resize' }}
        onMouseDown={(e) => handleResizeStart('s', e)}
      />
      <div
        style={{ ...edgeStyle, left: 0, top: '12px', bottom: '12px', width: '6px', cursor: 'w-resize' }}
        onMouseDown={(e) => handleResizeStart('w', e)}
      />
      <div
        style={{ ...edgeStyle, right: 0, top: '12px', bottom: '12px', width: '6px', cursor: 'e-resize' }}
        onMouseDown={(e) => handleResizeStart('e', e)}
      />

      {/* Resize handles - corners */}
      <div
        style={{ ...cornerStyle, top: 0, left: 0, cursor: 'nw-resize' }}
        onMouseDown={(e) => handleResizeStart('nw', e)}
      />
      <div
        style={{ ...cornerStyle, top: 0, right: 0, cursor: 'ne-resize' }}
        onMouseDown={(e) => handleResizeStart('ne', e)}
      />
      <div
        style={{ ...cornerStyle, bottom: 0, left: 0, cursor: 'sw-resize' }}
        onMouseDown={(e) => handleResizeStart('sw', e)}
      />
      <div
        style={{ ...cornerStyle, bottom: 0, right: 0, cursor: 'se-resize' }}
        onMouseDown={(e) => handleResizeStart('se', e)}
      />

      <ApolloProvider client={apolloClient}>
        <ThemeProvider value={themeContextValue}>
          <Chat />
        </ThemeProvider>
      </ApolloProvider>
    </div>
  );
};
